/*---------- Deps, Hooks & CSS ----------*/
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import styles from "./EventSetupStatusManager.module.css"

/*---------- Services ----------*/
import * as progressService from '../../../services/progressService'
import * as clientEventService from '../../../services/clientEventService'

/*---------- Components ----------*/
import Card from "../../../components/Home/StatusSubpages/Card"
import LoadingSpinner from '../../../components/Miscellaneous/LoadingSpinner'
import ProgressSteps from '../../../components/Miscellaneous/ProgressSteps'

/*---------- Images ----------*/
import LoadRegistrants from "../../../components/Home/StatusSubpages/EventSetupImages/RegistrantsLoaded.jpg"
import DataFiltering from "../../../components/Home/StatusSubpages/EventSetupImages/dataFiltering.jpg"
import DataTable from "../../../components/Home/StatusSubpages/EventSetupImages/dataTable.jpg"
import Webhook from "../../../components/Home/StatusSubpages/EventSetupImages/Webhook.png"
import PostToERS from "../../../components/Home/StatusSubpages/EventSetupImages/PostToERS.jpg"

const EventSetupStatusManager = () => {

    const location = useLocation()
    const passedState = location.state
    const [clientEventInfo, setClientEventInfo] = useState({})
    const clientEventInfoRef = useRef(null);
    const [changeMade, setChangeMade] = useState(false)
    if (clientEventInfoRef === null || clientEventInfoRef.current === null) {
        if (!passedState || !passedState.clientName) {
            clientEventInfoRef.current = clientEventService.getClientEvent();
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            clientEventInfoRef.current = passedData
        }
    }
    useEffect(() => {
        // run events fetch every x seconds, if its too fast, it won't run - gets stuck
        const interval = setInterval(() => checkClientEvent(), (30 * 1000))
        return () => clearInterval(interval)
    }, [])
    const checkClientEvent = () => {
        // Update clientevent with every render when its value has changed.
        let savedClientEvent = clientEventService.getClientEvent()
        if (clientEventInfoRef.current && clientEventInfoRef.current.eventId !== undefined &&
            savedClientEvent && savedClientEvent.eventId !== undefined &&
            savedClientEvent.eventId !== clientEventInfoRef.current.eventId) {
            setClientEventInfo(savedClientEvent)
            clientEventInfoRef.current = savedClientEvent;
            setChangeMade(true)
        }
    }
    useEffect(() => {
        if (changeMade && clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined
            && clientEventInfo.eventId !== undefined && clientEventInfo.eventName !== undefined) {
            setChangeMade(false)
            setLoaded(false)
            fetchData()
        }

    }, [clientEventInfo])
    /*---------- State ----------*/
    const [client, setClient] = useState(
        clientEventInfo.client
            ? clientEventInfo.client
            : clientEventInfo.clientId
                ? clientEventInfo.clientId
                : ""
    ) 
    const [event, setEvent] = useState(
        clientEventInfo.event
            ? clientEventInfo.event
            : clientEventInfo.eventId
                ? clientEventInfo.eventId
                : ""
    ) 
    const [clientEventObject, setClientEventObject] = useState({})
    const [loaded, setLoaded] = useState(false)

    // Required Setup
    const [registrantsLoadedProgress, setRegistrantsLoadedProgress] = useState("Loading...")
    const [eventFilteringProgress, setEventFilteringProgress] = useState("Loading...")
    const [viewGenerationProgress, setViewGenerationProgress] = useState("Loading...")

    // Optional Setup
    const [webhookSetupProgress, setWebhookSetupProgress] = useState("Loading...")
    const [postToERSSetupProgress, setPostToERSSetupProgress] = useState("Loading...")

    // Progress Steps
    const [requiredCompletionCount, setRequiredCompletionCount] = useState(1)
    const [optionalCompletionCount, setOptionalCompletionCount] = useState(1)

    /*---------- Constants ----------*/
    // General
    const clientName = clientEventInfo && clientEventInfo.clientName
        ? clientEventInfo.clientName
        : ""

    const eventName = clientEventInfo && clientEventInfo.eventName
            ? clientEventInfo.eventName
            : ""
    // curently does nothing, do we need it?
    const ERSName = clientEventInfo && clientEventInfo.eventRegistrationSystem !== undefined
        ? clientEventInfo.eventRegistrationSystem
        : "ERS"

    const clientId = clientEventInfo && clientEventInfo.clientId
            ? clientEventInfo.clientId
            : ""

    const eventId = clientEventInfo &&  clientEventInfo.eventId
            ? clientEventInfo.eventId
            : ""

    // Progress Steps 
    const requiredSteps = [
        { label: 'Load Event Registrants', step: 1 },
        { label: 'Event Filtering', step: 2 },
        { label: 'View Generation', step: 3 },
    ]

    const optionalSteps = [
        { label: 'Webhook Setup', step: 1 },
        { label: 'ERS Posting', step: 2 },
    ]

    /*---------- Functions ----------*/
    // Step Calculations
    const checkRequiredCompletion = () => {
        if (registrantsLoadedProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (eventFilteringProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (viewGenerationProgress === "Success") setRequiredCompletionCount(count => count + 1)
    }

    const checkOptionalCompletion = () => {
        if (webhookSetupProgress === "Success") setOptionalCompletionCount(count => count + 1)
        if (postToERSSetupProgress === "Success") setOptionalCompletionCount(count => count + 1)
    }

    const checkLoaded = () => {
        if (registrantsLoadedProgress !== "Loading..."
            && eventFilteringProgress !== "Loading..."
            && viewGenerationProgress !== "Loading..."
            && webhookSetupProgress !== "Loading..."
            && postToERSSetupProgress !== "Loading..."
        )
            setLoaded(true)
    }

    const fetchData = async () => {
        
        const clientEventObj = {
            ClientId: clientId,
            EventId: eventId,
        }
        setClientEventObject(clientEventObj)
        try {
            // Fire all progress requests in parallel instead of sequentially
            const [
                registrantsProgress,
                filteringProgress,
                viewProgress,
                webhookProgress,
                postToERSProgress,
            ] = await Promise.all([
                progressService.getRegistrantsLoadedProgress(clientEventObj),
                progressService.getEventFilteringProgress(clientEventObj),
                progressService.getViewGeneratedProgress(clientEventObj),
                progressService.getWebhookSetupProgress(clientEventObj),
                progressService.getPostToERSProgress(clientEventObj),
            ])

            setRegistrantsLoadedProgress(registrantsProgress)
            setEventFilteringProgress(filteringProgress)
            setViewGenerationProgress(viewProgress)
            setWebhookSetupProgress(webhookProgress)
            setPostToERSSetupProgress(postToERSProgress)
        }
        catch (error) {
            throw error
        }
    }

    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined) {
            fetchData()
        }
    }, [clientEventInfo])
    useEffect(() => {
        // store into local object
        if (!passedState || !passedState.clientName) {
            //let localclientevent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current) {
                setClientEventInfo(clientEventInfoRef.current)
            }
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            setClientEventInfo(passedData)
            clientEventInfoRef.current = passedData;
        }
    }, [])
    useEffect(() => {
        checkLoaded()
        checkRequiredCompletion()
        checkOptionalCompletion()
    }, [postToERSSetupProgress])
    
    return (
        <>
        {
                loaded ?                
                    <div id={styles.statusSubpage}>
                        <div className={styles.requiredSetupHeader}>
                            <h2 className={styles.requiredSetupHeading}>Required Setup</h2>
                            <ProgressSteps
                                steps={requiredSteps}
                                activeStep={requiredCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={LoadRegistrants}
                                header="Load Event Registrants"
                                content={`Load registrants for the ${eventName}`}
                                styles={styles}
                                linkName={`/EventLoad/${eventId}`}
                                linkState={clientEventInfo}
                                disabledBool={false} // hardcoded as it's the first required step of an event --- can't be disabled
                            />
                            <Card
                                dataImage={DataFiltering}
                                header="Event Filtering"
                                content={`Filter data from ${ERSName} for the ${eventName}`}
                                styles={styles}
                                linkName={`/EventSetting/EventFilterList`}
                                linkState={clientEventInfo}
                                disabledBool={registrantsLoadedProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={DataTable}
                                header="View Generation"
                                content={`Configure filtered attendee data structure for the ${eventName}`}
                                styles={styles}
                                linkName={`/ViewGenerate`}
                                linkState={clientEventInfo}
                                disabledBool={eventFilteringProgress !== "Success" ? true : false}
                            />
                        </div>

                        <div className={styles.optionalSetupHeader}>
                            <h2 className={styles.optionalSetupHeading}>Optional Setup</h2>
                            <ProgressSteps
                                steps={optionalSteps}
                                activeStep={optionalCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={Webhook}
                                header="Webhook Setup"
                                content={`Setup webhook data transferal for ${eventName}`}
                                styles={styles}
                                linkName={`/EventSetting/WebHookList`}
                                linkState={clientEventInfo}
                                disabledBool={viewGenerationProgress !== "Success" ? true : false}
                            />
                            <Card
                                dataImage={PostToERS}
                                header="ERS Posting"
                                content={`Configure PostToERS capabilities for the ${eventName}`}
                                styles={styles}
                                linkName={`/EventLoad/${eventId}`} // There's no page for this yet
                                linkState={clientEventInfo}
                                disabledBool={webhookSetupProgress !== "Success" ? true : false}
                            />
                        </div>
                    </div>
            :
                <LoadingSpinner />
        }
        </>
    );
}

export default EventSetupStatusManager;